import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import {
  acceptedFileTypes,
  baseClientTypeOptions,
  buildClientPayloadFromForm,
  MAX_LOGO_FILE_SIZE_BYTES,
  MAX_LOGO_FILE_SIZE_MB,
  DEFAULT_CLIENT_TEXT_COLOR,
  normalizeWorkspaceValue,
  type ClientFormValues,
} from '@/features/client/clientFormShared'
import {
  useCreateClientMutation,
  usePbiGroups,
  useUploadClientLogoMutation,
} from '@/features/client/hooks'
import { useToast } from '@/contexts/ToastContext'
import { ClientThemeColorFields } from '@/features/client/components/ClientThemeColorFields'
import { fileToBase64 } from '@/lib/files'
import { formatPhone, normalizePhone } from '@/lib/phone'
import { updateSession } from '@/lib/session'
import { entityNameSchema, requiredEmailSchema, shortTextSchema, zodAntdValidator } from '@/lib/validation'

const defaultCreateFormValues: Partial<ClientFormValues> = {
  name: '',
  phone: '',
  email: '',
  principalContact: '',
  address: '',
  street: '',
  suburb: '',
  state: '',
  postcode: '',
  powerBIWorkspace: undefined,
  powerBIWorkspaceName: '',
  statusId: 1,
  typeId: 1,
  showProjectStatus: false,
  showAdvisoryMapping: false,
  textColor: DEFAULT_CLIENT_TEXT_COLOR,
}

const clientNameSchema = entityNameSchema('Client name')
const principalContactSchema = shortTextSchema('Principal contact')
const emailSchema = requiredEmailSchema('Email')
const addressSchema = shortTextSchema('Current Address', { required: false })
const streetSchema = shortTextSchema('Street', { required: false })
const suburbSchema = shortTextSchema('Suburb/City', { required: false })
const stateSchema = shortTextSchema('State', { required: false })
const postcodeSchema = shortTextSchema('Postcode', { required: false })

type CreateClientModalProps = {
  open: boolean
  onClose: () => void
  isAdmin: boolean
}

export function CreateClientModal({
  open,
  onClose,
  isAdmin,
}: CreateClientModalProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()

  const [logoBase64, setLogoBase64] = useState('')
  const [logoContentType, setLogoContentType] = useState('')
  const [logoFileName, setLogoFileName] = useState('')
  const [logoError, setLogoError] = useState('')

  const { data: pbiGroups = [], isLoading: pbiGroupsLoading } = usePbiGroups()
  const createMutation = useCreateClientMutation()
  const uploadClientLogoMutation = useUploadClientLogoMutation()

  const [form] = Form.useForm<ClientFormValues>()

  const typeOptions = useMemo(
    () => (isAdmin ? [...baseClientTypeOptions, { label: 'Advisor', value: 3 as const }] : baseClientTypeOptions),
    [isAdmin]
  )

  const resetLogoState = () => {
    setLogoBase64('')
    setLogoContentType('')
    setLogoFileName('')
    setLogoError('')
  }

  useEffect(() => {
    if (!open) return
    resetLogoState()
    form.resetFields()
    form.setFieldsValue(defaultCreateFormValues as ClientFormValues)
  }, [open, form])

  const closeModal = () => {
    onClose()
    form.resetFields()
    resetLogoState()
  }

  const workspaceOptions = useMemo(() => {
    const options = pbiGroups.map((group) => ({ value: String(group.value), label: group.name }))
    const workspaceValue = normalizeWorkspaceValue(form.getFieldValue('powerBIWorkspace'))
    const workspaceName = form.getFieldValue('powerBIWorkspaceName')

    if (workspaceValue && !options.some((option) => option.value === workspaceValue)) {
      options.push({
        value: workspaceValue,
        label: workspaceName || workspaceValue,
      })
    }

    return options
  }, [form, pbiGroups])

  const saveClient = async () => {
    try {
      const values = await form.validateFields()
      const payload = buildClientPayloadFromForm(values, pbiGroups, logoBase64)

      const created = await createMutation.mutateAsync(payload)
      const createdClientId = created?.clientId || created?.ClientId
      const createdName = created?.name || created?.Name || values.name

      if (!createdClientId) {
        addToast('Unable to create client', 'error')
        return
      }

      if (logoBase64) {
        const uploaded = await uploadClientLogoMutation.mutateAsync({
          clientId: createdClientId,
          base64image: logoBase64,
          contentType: logoContentType,
        })
        if (!uploaded) {
          addToast('Client created, but logo upload failed', 'warning')
        }
      }

      addToast('New client created successfully', 'success')
      closeModal()

      updateSession({
        currentClientId: createdClientId,
        currentClientName: createdName,
        currentClientTypeId: values.typeId,
        currentClientShowProjectStatus: !!values.showProjectStatus,
        currentClientShowAdvisoryMapping: !!values.showAdvisoryMapping,
      })
      navigate(`/clients/${createdClientId}`)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to save client'
      addToast(message, 'error')
    }
  }

  const logoUploadProps: UploadProps = {
    accept: acceptedFileTypes.join(','),
    showUploadList: false,
    beforeUpload: async (file) => {
      if (!acceptedFileTypes.includes(file.type)) {
        setLogoError('Acceptable formats are JPEG, JPG, PNG, TIFF, WEBP, BMP, or GIF.')
        return Upload.LIST_IGNORE
      }
      if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
        setLogoError(`Logo file is too large. Maximum file size is ${MAX_LOGO_FILE_SIZE_MB}MB.`)
        return Upload.LIST_IGNORE
      }
      try {
        const base64 = await fileToBase64(file as File)
        setLogoBase64(base64)
        setLogoContentType(file.type)
        setLogoFileName(file.name)
        setLogoError('')
      } catch {
        setLogoError('Unable to process selected logo file.')
      }
      return false
    },
  }

  return (
    <Modal
      width={780}
      open={open}
      title="Add New Client"
      onCancel={closeModal}
      onOk={() => void saveClient()}
      okText="Create"
      okButtonProps={{
        loading: createMutation.isPending || uploadClientLogoMutation.isPending,
      }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space>
          <CancelBtn />
          <OkBtn />
        </Space>
      )}
    >
      <Form form={form} layout="vertical">
        {logoError && <Alert type="error" showIcon className="mb-3" message={logoError} />}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item name="name" label="Client Name" rules={[{ validator: zodAntdValidator(clientNameSchema) }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="powerBIWorkspace"
            label="Workspace ID"
            rules={[{ required: true, message: 'Workspace ID is required' }]}
          >
            <Select
              loading={pbiGroupsLoading}
              options={workspaceOptions}
              showSearch
              optionFilterProp="label"
              onChange={(value, option) => {
                const nextValue = Array.isArray(value) ? value[0] : value
                const nextOption = Array.isArray(option) ? option[0] : option
                form.setFieldValue('powerBIWorkspace', normalizeWorkspaceValue(nextValue))
                form.setFieldValue(
                  'powerBIWorkspaceName',
                  typeof nextOption?.label === 'string' ? nextOption.label : ''
                )
              }}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item
            name="principalContact"
            label="Principal Contact"
            rules={[{ validator: zodAntdValidator(principalContactSchema) }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ validator: zodAntdValidator(emailSchema) }]}>
            <Input />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              { required: true, message: 'Phone is required' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()
                  return normalizePhone(value).length === 10
                    ? Promise.resolve()
                    : Promise.reject(new Error('Phone number must be exactly 10 digits.'))
                },
              },
            ]}
            getValueFromEvent={(event) => normalizePhone(event?.target?.value).slice(0, 10)}
            getValueProps={(value) => ({ value: formatPhone(value) === '—' ? '' : formatPhone(value) })}
          >
            <Input />
          </Form.Item>
          <Form.Item name="typeId" label="Client Type">
            <Select options={typeOptions} />
          </Form.Item>
        </div>

        <Form.Item label="Logo Upload">
          <Space direction="vertical" size={6} className="w-full">
            <Upload {...logoUploadProps}>
              <Button icon={<UploadOutlined />}>Browse Logo</Button>
            </Upload>
            <Typography.Text type="secondary">
              Accepted formats: JPEG, JPG, PNG, TIFF, WEBP, BMP, GIF. Max {MAX_LOGO_FILE_SIZE_MB}MB.
            </Typography.Text>
            {logoFileName && (
              <Space direction="vertical" size={8} className="w-fit">
                <Typography.Text type="secondary">{logoFileName}</Typography.Text>
                <Button onClick={resetLogoState} className="w-fit">
                  Remove Selected Logo
                </Button>
              </Space>
            )}
          </Space>
        </Form.Item>

        <ClientThemeColorFields />

        {isAdmin && (
          <Form.Item name="showProjectStatus" valuePropName="checked">
            <Checkbox>Show Project Status Menu</Checkbox>
          </Form.Item>
        )}
        {isAdmin && (
          <Form.Item name="showAdvisoryMapping" valuePropName="checked">
            <Checkbox>Show Advisory Mapping Menu</Checkbox>
          </Form.Item>
        )}
        <Form.Item name="address" label="Current Address" rules={[{ validator: zodAntdValidator(addressSchema) }]}>
          <Input />
        </Form.Item>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item name="street" label="Street" rules={[{ validator: zodAntdValidator(streetSchema) }]}>
            <Input />
          </Form.Item>
          <Form.Item name="suburb" label="Suburb/City" rules={[{ validator: zodAntdValidator(suburbSchema) }]}>
            <Input />
          </Form.Item>
          <Form.Item name="state" label="State" rules={[{ validator: zodAntdValidator(stateSchema) }]}>
            <Input />
          </Form.Item>
          <Form.Item name="postcode" label="Postcode" rules={[{ validator: zodAntdValidator(postcodeSchema) }]}>
            <Input />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
